/**
 * Clinical audio constants.
 *
 * These values are measured and tuned, not preferences — they came out of the
 * existing deployment and changing one changes what a clinician hears. Each is
 * annotated with why it is what it is.
 */

/** The AudioContext runs at 48 kHz; Opus is native at this rate. */
export const CONTEXT_SAMPLE_RATE = 48_000;

/** Doctor-facing gain range (architecture §1.1). */
export const MIN_GAIN_DB = 0;
export const MAX_GAIN_DB = 40;
export const DEFAULT_GAIN_DB = 20;

/**
 * Gain ramp time constant for `setTargetAtTime`. Long enough that a slider drag
 * does not produce zipper noise, short enough to feel immediate.
 */
export const GAIN_RAMP_TIME_CONSTANT = 0.05;

/**
 * Limiter, not a compressor.
 *
 * knee = 0 with a high ratio makes it mathematically transparent below the
 * threshold, so it only ever catches peaks. A compressor here would alter the
 * S1/S2 amplitude relationship, which is diagnostic information.
 */
export const LIMITER_THRESHOLD_DB = -3;
export const LIMITER_KNEE_DB = 0;
export const LIMITER_RATIO = 20;
export const LIMITER_ATTACK_S = 0.001;
export const LIMITER_RELEASE_S = 0.1;

/**
 * Butterworth Q, cascaded twice to make a 4th-order Linkwitz-Riley response.
 * Monotonic by construction — it cannot invent a peak that a clinician would
 * read as a murmur.
 */
export const FILTER_Q = Math.SQRT1_2;
export const FILTER_STAGES = 2;

/** Auscultation bands. */
export type FilterMode = 'off' | 'bell' | 'diaphragm' | 'extended' | 'lung';

export interface FilterBand {
  highPassHz: number;
  lowPassHz: number;
  label: string;
}

export const FILTER_BANDS: Record<Exclude<FilterMode, 'off'>, FilterBand> = {
  bell: { highPassHz: 20, lowPassHz: 200, label: 'bell (20-200 Hz)' },
  diaphragm: { highPassHz: 100, lowPassHz: 500, label: 'diaphragm (100-500 Hz)' },
  extended: { highPassHz: 20, lowPassHz: 1000, label: 'extended (20-1000 Hz)' },
  lung: { highPassHz: 100, lowPassHz: 2000, label: 'lung (100-2000 Hz)' },
};

/**
 * Default band.
 *
 * UI_NEW defaults to 'off'. This app defaults to 'bell' because §5.2 identifies
 * 20–150 Hz as where S1/S2 live, and shipping with no band-pass means room noise
 * and handling rumble compete for the same headroom as the heart sounds. 'bell'
 * (20–200 Hz) covers the diagnostic band with margin.
 */
export const DEFAULT_FILTER_MODE: FilterMode = 'bell';

export function getFilterBand(mode: FilterMode): FilterBand | null {
  return mode === 'off' ? null : FILTER_BANDS[mode];
}

/** dB → linear amplitude. */
export function dbToLinear(db: number): number {
  return Math.pow(10, db / 20);
}
